import React, { useState, useEffect, useMemo } from 'react';
import { DealerLedgerViewModel, LedgerPeriod } from '@lumora/core';
import { useViewModel } from '@lumora/ui';
import { client } from '../../client';
import type Car from '../../models/Car';
import { formatCurrency } from '../component/CarCard';

interface AdminSalesProps {
    inventory: Car[];
}

const AdminSales = ({ inventory }: AdminSalesProps) => {
    const [month, setMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
    const [year, setYear] = useState(String(new Date().getFullYear()));
    const ledgerVm = useMemo(() => new DealerLedgerViewModel(client), []);
    const { sales, expenses } = useViewModel(ledgerVm);
    const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
    const [isInternalRepair, setIsInternalRepair] = useState(false);

    const fetchLedger = () => ledgerVm.loadLedger(new LedgerPeriod(month, year));

    useEffect(() => {
        void fetchLedger();
    }, [month, year]);

    const handleAddExpense = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);

        const ok = await ledgerVm.recordExpense({
            category: String(formData.get('category') || ''),
            description: String(formData.get('description') || ''),
            amount: Number(formData.get('amount')),
            date: String(formData.get('date') || ''),
            listingId: String(formData.get('carId') || '') || null,
            internalRepair: isInternalRepair,
        });
        if (ok) {
            setIsExpenseModalOpen(false);
            setIsInternalRepair(false);
            void fetchLedger();
        } else {
            console.error(ledgerVm.state.error);
        }
    };

    const totalRevenue = sales.reduce((sum, s) => sum + s.soldPrice, 0);
    const totalPurchaseCost = sales.reduce((sum, s) => sum + s.purchaseCost, 0);
    const totalCosts = expenses.reduce((sum, e) => sum + e.amount, 0);
    const totalProfit = totalRevenue - totalPurchaseCost - totalCosts;

    // Combine and sort ledger by date
    const ledger = [
        ...sales.map(sale => ({ kind: 'SALE' as const, date: sale.soldDate, sale })),
        ...expenses.map(expense => ({ kind: 'EXPENSE' as const, date: expense.date, expense }))
    ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-4xl font-black italic tracking-tighter uppercase text-gray-900 leading-none">Financial Ledger</h1>
                    <p className="text-gray-500 font-bold uppercase text-[10px] tracking-widest mt-1">Sales & Operational Expenses</p>
                </div>
                <div className="flex items-center space-x-4 w-full sm:w-auto">
                    <select value={month} onChange={(e) => setMonth(e.target.value)} className="border-2 border-gray-200 rounded-xl p-3 font-bold text-sm outline-none w-full sm:w-auto bg-white">
                        <option value="all">All Months</option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                            <option key={m} value={String(m).padStart(2, '0')}>
                                {new Date(0, m - 1).toLocaleString('default', { month: 'long' })}
                            </option>
                        ))}
                    </select>
                    <input 
                        type="number" value={year} onChange={(e) => setYear(e.target.value)} 
                        className="border-2 border-gray-200 rounded-xl p-3 font-bold text-sm outline-none w-24 bg-white"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-center">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Gross Revenue</span>
                    <span className="text-3xl font-black text-gray-900 tracking-tighter">{formatCurrency(totalRevenue)}</span>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-center">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Total Expenses</span>
                    <span className="text-3xl font-black text-red-600 tracking-tighter">{formatCurrency(totalCosts)}</span>
                </div>
                <div className="bg-gray-900 p-6 rounded-2xl shadow-lg shadow-gray-200 flex flex-col justify-center relative overflow-hidden">
                    <div className="relative z-10">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Net Income (Period)</span>
                        <span className={`text-3xl font-black tracking-tighter ${totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {formatCurrency(totalProfit)}
                        </span>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <h3 className="text-xl font-bold uppercase tracking-widest text-gray-800">Transaction History</h3>
                    <button 
                        onClick={() => setIsExpenseModalOpen(true)}
                        className="bg-gray-900 text-white px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black transition"
                    >
                        + Log Expense
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 text-[10px] font-black uppercase tracking-widest text-gray-400 border-b border-gray-200">
                                <th className="p-4">Date</th>
                                <th className="p-4">Description</th>
                                <th className="p-4 text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm">
                            {ledger.map((item, idx) => (
                                <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                                    <td className="p-4 font-bold text-gray-500 whitespace-nowrap">{item.date}</td>
                                    <td className="p-4">
                                        {item.kind === 'SALE' ? (
                                            <div>
                                                <span className="font-black text-green-700 uppercase text-xs tracking-widest bg-green-100 px-2 py-0.5 rounded mr-2">SOLD</span>
                                                <span className="font-bold">{item.sale.year} {item.sale.make} {item.sale.model}</span>
                                                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                                                    Buyer: {item.sale.buyerName || 'N/A'} • Cost Basis: {formatCurrency(item.sale.purchaseCost)} • Profit: <span className={item.sale.profit() >= 0 ? 'text-green-600' : 'text-red-600'}>{formatCurrency(item.sale.profit())}</span>
                                                </div>
                                                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                                                    Sold by: N/A
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <span className="font-black text-red-700 uppercase text-xs tracking-widest bg-red-100 px-2 py-0.5 rounded mr-2">
                                                    {item.expense.category === 'Internal Revenue' ? 'INCOME' : 'EXP'}
                                                </span>
                                                <span className="font-bold">{item.expense.description}</span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-4 text-right font-black tracking-tight">
                                        {item.kind === 'SALE' ? (
                                            <span className="text-green-600">{formatCurrency(item.sale.soldPrice)}</span>
                                        ) : (
                                            <span className={item.expense.amount < 0 ? 'text-green-600' : 'text-red-600'}>
                                                {item.expense.amount < 0 ? '' : '-'}{formatCurrency(Math.abs(item.expense.amount))}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {ledger.length === 0 && (
                                <tr>
                                    <td colSpan={3} className="p-10 text-center text-gray-400 font-bold uppercase text-xs tracking-widest">No transactions found for this period.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {isExpenseModalOpen && (
                <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <h3 className="text-xl font-bold uppercase tracking-widest">Log Expense</h3>
                            <button onClick={() => setIsExpenseModalOpen(false)} className="text-gray-400 hover:text-gray-900">&times;</button>
                        </div>
                        <form onSubmit={handleAddExpense} className="p-6 space-y-4">
                            <div>
                                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Category</label>
                                <select name="category" required className="w-full border-2 border-gray-100 rounded-xl p-3 focus:border-gray-900 outline-none font-bold text-sm bg-white">
                                    <option value="Repair">Repair / Maintenance</option>
                                    <option value="Marketing">Marketing & Ads</option>
                                    <option value="Operations">Operations / Utilities</option>
                                    <option value="Payroll">Payroll</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            
                            <div>
                                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Linked Vehicle (Optional)</label>
                                <select name="carId" className="w-full border-2 border-gray-100 rounded-xl p-3 focus:border-gray-900 outline-none font-bold text-sm bg-white">
                                    <option value="">None (General Expense)</option>
                                    {inventory.filter(c => !c.archive).map(c => (
                                        <option key={c.id} value={c.id}>{c.year} {c.make} {c.model}</option>
                                    ))}
                                </select>
                            </div>

                            <label className="flex items-center space-x-3 p-3 bg-blue-50 border border-blue-100 rounded-xl cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    checked={isInternalRepair} 
                                    onChange={(e) => setIsInternalRepair(e.target.checked)}
                                    className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500"
                                />
                                <div className="flex flex-col">
                                    <span className="text-xs font-black uppercase tracking-widest text-blue-900">Internal Dealership Repair</span>
                                    <span className="text-[9px] font-bold text-blue-600">Logs expense to car, pays profit to shop!</span>
                                </div>
                            </label>

                            <div>
                                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Description</label>
                                <input name="description" type="text" required placeholder="e.g. Replaced brakes" className="w-full border-2 border-gray-100 rounded-xl p-3 focus:border-gray-900 outline-none font-medium" />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Amount ($)</label>
                                    <input name="amount" type="number" step="0.01" min="0" required className="w-full border-2 border-gray-100 rounded-xl p-3 focus:border-red-500 outline-none font-black text-lg text-red-600" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Date</label>
                                    <input name="date" type="date" required defaultValue={new Date().toISOString().split('T')[0]} className="w-full border-2 border-gray-100 rounded-xl p-3 focus:border-gray-900 outline-none font-bold" />
                                </div>
                            </div>

                            <div className="pt-4 flex space-x-3">
                                <button type="button" onClick={() => setIsExpenseModalOpen(false)} className="flex-1 bg-gray-100 text-gray-600 font-bold uppercase text-xs tracking-widest py-3 rounded-xl hover:bg-gray-200 transition">Cancel</button>
                                <button type="submit" className="flex-2 text-white bg-red-600 font-bold uppercase text-xs tracking-widest py-3 rounded-xl shadow-lg hover:bg-red-700 transition">Save Expense</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
};

export default AdminSales;
